import { TranslocoDirective, TranslocoModule, TranslocoService, provideTranslocoScope, translate } from '@jsverse/transloco';
﻿import { AfterViewInit, Component, inject, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { TableView, TableColumnInfo, TypeOfColumn, SortColumn, LoadingSpinner } from "design-system";
import { debounceTime } from 'rxjs';
import { BreakpointObserver } from '@angular/cdk/layout';
import { Q8Breakpoints, HeaderService, DynamicRouteService, BYPASS_BASE_URL } from 'core';
import { TranslocoCurrencyPipe } from '@jsverse/transloco-locale';
import { MatCardModule } from '@angular/material/card';
import { MatDividerModule } from '@angular/material/divider';
import { DatePipe, NgClass } from '@angular/common';
import { MatChipsModule, MatChipListboxChange } from '@angular/material/chips';
import {MatFormFieldModule} from '@angular/material/form-field';
import {MatInputModule} from '@angular/material/input';
import {MatAutocompleteModule} from '@angular/material/autocomplete';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { AsyncPipe } from '@angular/common';
import { Observable, startWith, map, finalize } from 'rxjs';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { ChipListino } from '../../components/chip-listino/chip-listino';
import { IconAlert } from '../../components/icon-alert/icon-alert';
import {MatButtonToggleModule} from '@angular/material/button-toggle';
import { CustomersFilter } from '../../interfaces/customers-filter';
import { ChipsData } from '../../interfaces/customers.model';
import { CustomersFilterDialogComponent } from '../../components/dialog-components/customers-filter-dialog-component/customers-filter-dialog-component';
import { CustomersSortDialogComponent } from '../../components/dialog-components/customers-sort-dialog-component/customers-sort-dialog-component';
import { ChipDataService } from '../../services/customers.service';
import { ZoneItemService } from '../../services/lookup-tables/zone.service';
import { CustomerItem } from './customers-list.model';
import { HttpClient, HttpContext } from '@angular/common/http';
import { MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { MatBottomSheet, MatBottomSheetRef } from '@angular/material/bottom-sheet';
import { GoogleMap, MapAdvancedMarker, MapInfoWindow, MapMarkerClusterer, MapPolygon, Algorithm } from '@angular/google-maps'
import { ActivatedRoute, Router } from '@angular/router';
import { CUSTOMERS_DESCRIBE } from '../constants';
import { ZONE_BY_CAP } from '../zone-by-cap';
import { ZoneItem } from '../../interfaces/lookup-tables/zone.model';
import { AgenteLookupItem, AgentiService } from '../../services/lookup-tables/agenti.service';
import { IspettoreLookupItem, IspettoriService } from '../../services/lookup-tables/ispettori.service';
import { TipoZonaLookupItem, TipoZonaService } from '../../services/lookup-tables/tipo-zona.service';

// Global esposto dallo script @googlemaps/markerclusterer caricato in index.html
declare const markerClusterer: {
  SuperClusterViewportAlgorithm: new (options: { viewportPadding?: number; maxZoom?: number; radius?: number }) => Algorithm;
};

// Relativo al base href, come le traduzioni: vale anche in una virtual directory
const ZONE_POLYGONS_URL = 'assets/customers/map-polygons/capzone_all.geojson';

// Struttura minima del GeoJSON dei CAP: ogni feature è un MultiPolygon con coordinate [lng, lat]
interface CapZoneCollection {
  features: {
    properties: { CAP: string };
    geometry: { coordinates: number[][][][] };
  }[];
}

// Colore di riempimento per codice zona (stesso schema dell'app Flutter, alpha 0x55 ≈ 0.33)
const ZONE_COLORS: Record<string, string> = {
  '006': '#3F51B5',
  '007': '#4CAF50',
  '016': '#FF9800',
  '368': '#00CCFF',
};
const ZONE_FILL_OPACITY = 0.33;

// Un poligono di zona: paths[0] è il contorno esterno, gli eventuali successivi sono buchi
interface ZonePolygon {
  cap: string;
  zone: string;
  paths: google.maps.LatLngLiteral[][];
  options: google.maps.PolygonOptions;
}

export enum CustomersType { ACTIVE, ORDER_IN_PROG, EXPIRED_U30, EXPIRED_O30 }

const SORT_PROPERTY_MAP: Record<string, string> = {
  ragioneSociale: 'RagioneSociale',
  localita: 'Localita',
  zonaAgente: 'NomeZona',
  esposizione: 'Esposizione.Valore',
  affidamento: 'Affidamento.Valore',
  fatturatoRolling12m: 'FatturatoRolling12m',
  dataUltimaFattura: 'DataUltimaFattura',
  alert: 'Alert.Valore',
};

interface StatusChip {
  status: CustomersType;
  icon: string;
  iconVariant: string;
  labelKey: string;
  countProp: number;
  amount?: number;
}

@Component({
  providers: [provideTranslocoScope('customers')],
  selector: 'cus-customers-list',
  imports: [
    TableView, MatCardModule, MatDividerModule,
    DatePipe,
    MatChipsModule, TranslocoModule, MatFormFieldModule,
    MatInputModule, MatAutocompleteModule, MatDatepickerModule,
    ReactiveFormsModule, AsyncPipe,
    NgClass,
    MatCheckboxModule, MatButtonModule, MatButtonToggleModule,
    ChipListino, IconAlert, TranslocoDirective, TranslocoCurrencyPipe,
    MatPaginatorModule, GoogleMap, MapAdvancedMarker, MapInfoWindow, MapMarkerClusterer, MapPolygon,
    LoadingSpinner
  ],
  templateUrl: './customers-list.html',
  styleUrl: './customers-list.scss',
})
export class CustomersList implements OnInit, AfterViewInit, OnDestroy {
  statusChips: StatusChip[] = this.buildStatusChips();

  customers: CustomerItem[] = [];
  customersView: any[] = [];
  chipsData?: ChipsData;
  isMobile = false;
  isDesktop = false;
  columnsInfo: TableColumnInfo[] = [];
  customersLoading = false;
  private customersLoadRequestId = 0;

  totalItems = 0;
  pageSize = 10;
  pageIndex = 0; // zero-based per mat-paginator
  currentSortColumns: SortColumn[] = [];

  // Stato dedicato all'infinite scroll della advisor bar, separato dalla paginazione della tabella.
  // advisorCustomers è la lista accumulata (append), non sostituita ad ogni pagina.
  advisorCustomers: CustomerItem[] = [];
  advisorPageIndex = 0;     // pagina corrente dell'infinite scroll (incrementata a ogni caricamento)
  advisorPageSize = 25;     // quanti item caricare per volta
  advisorTotalItems = 0;    // totale restituito dall'API, usato per capire se ci sono altre pagine
  advisorLoading = false;   // lock per evitare chiamate sovrapposte durante lo scroll

  mapAdvisorOpen = false;

  //DATI MAPPA
  @ViewChild(GoogleMap) googleMap?: GoogleMap;
  @ViewChild(MapInfoWindow) infoWindow: MapInfoWindow;
  @ViewChild('advisorSheet') advisorSheetTemplate!: TemplateRef<any>;
  private bottomSheetRef?: MatBottomSheetRef;
  center: google.maps.LatLngLiteral = { lat: 45.063105817727404, lng: 7.679272337002429};
  zoom: number = 17;
  mapOptions: google.maps.MapOptions = {
    disableDefaultUI: true,
    mapTypeControl: false,
    fullscreenControl: false,
    streetViewControl: false,
    zoomControl: false,
    rotateControl: false,
    scaleControl: false,
    keyboardShortcuts: false,
    clickableIcons: false,
  };
  mapMarkers: (CustomerItem & { mapPosition: google.maps.LatLngLiteral })[] = [];
  // Evita di disegnare <google-map> prima che loadMapMarkers() abbia risposto: se il
  // clusterer viene renderizzato con mapMarkers ancora vuoto va in errore (getClusters
  // su un indice supercluster non ancora inizializzato).
  mapMarkersLoaded = false;
  selectedMarker: CustomerItem | null = null;
  // L'algoritmo di default (SuperClusterAlgorithm) oltre il suo maxZoom (~16) smette di
  // raggruppare e attacca alla mappa tutti i marker, anche quelli fuori schermo, il che
  // porterebbe a problemi di performance con tanti markers. La variante "viewport"
  // calcola cluster e marker solo per l'area visibile (più un padding in pixel). Ogni
  // volta che ci si sposta sulla mappa il calcolo viene eseguito nuovamente, garantendo
  // che non venga perso nessun marker
  clusterAlgorithm: Algorithm = new markerClusterer.SuperClusterViewportAlgorithm({ viewportPadding: 128 });
  private userPositionRequested = false;

  zonePolygons: ZonePolygon[] = [];
  private zonePolygonsRequested = false;

  // Legge il GeoJSON dei CAP e tiene solo i poligoni dei CAP presenti in ZONE_BY_CAP.
  // Il file è un MultiPolygon per CAP: ogni poligono ha il contorno esterno seguito da
  // eventuali buchi, ognuno è una lista di [lng, lat].
  private loadZonePolygons() {
    if (this.zonePolygonsRequested) return;
    this.zonePolygonsRequested = true;

    this.http.get<CapZoneCollection>(
      ZONE_POLYGONS_URL,
      { context: new HttpContext().set(BYPASS_BASE_URL, true) }
    ).subscribe({
      next: (collection) => {
        const polygons: ZonePolygon[] = [];
        for (const feature of collection.features) {
          const cap = feature.properties.CAP;
          const zone = ZONE_BY_CAP[cap];
          if (!zone) continue;

          for (const polygon of feature.geometry.coordinates) {
            polygons.push({
              cap,
              zone,
              paths: polygon.map(ring => ring.map(([lng, lat]) => ({ lat, lng }))),
              options: {
                strokeColor: ZONE_COLORS[zone],
                strokeOpacity: 0.8,
                strokeWeight: 3,
                fillColor: ZONE_COLORS[zone],
                fillOpacity: ZONE_FILL_OPACITY,
                clickable: false,
              },
            });
          }
        }
        this.zonePolygons = polygons;
      },
      error: () => {
        // Permette di riprovare al prossimo ingresso in vista mappa
        this.zonePolygonsRequested = false;
      }
    });
  }

  // Chiede al browser la posizione dell'utente (prompt di permesso alla prima volta)
  // e centra la mappa lì. Se il permesso viene negato o la posizione non è
  // disponibile, la mappa resta sul centro di default.
  private requestUserPosition() {
    if (this.userPositionRequested || !navigator.geolocation) return;
    this.userPositionRequested = true;

    navigator.geolocation.getCurrentPosition(
      (position) => {
        this.center = {
          lat: position.coords.latitude,
          lng: position.coords.longitude,
        };
        this.googleMap?.googleMap?.panTo(this.center);
      },
      (error) => {
        console.warn('Geolocalizzazione non disponibile:', error.message);
      },
      { timeout: 10000, maximumAge: 60000 }
    );
  }

  focusOnCustomer(customer: CustomerItem) {
    this.bottomSheetRef?.dismiss();
    if (customer.latitudine == null || customer.longitudine == null) return;
    const pos = { lat: customer.latitudine, lng: customer.longitudine };
    this.googleMap?.googleMap?.panTo(pos);
    this.googleMap?.googleMap?.setZoom(18);
  }

  openInfoWindow(marker: MapAdvancedMarker, item: CustomerItem) {
    this.selectedMarker = item;
    this.infoWindow.open(marker);
  }

  // MapAdvancedMarker si attacca da solo alla mappa appena creato, ma il MarkerClusterer
  // gestisce solo i marker che entrano in un cluster calcolato: con l'algoritmo viewport
  // quelli fuori schermo resterebbero pin singoli, e più ne sono più si abbassano le
  // performance. Staccandoli subito dopo la creazione, la visibilità la decide solo il
  // clusterer.
  onMarkerInitialized(marker: google.maps.marker.AdvancedMarkerElement) {
    // Non elimina il pin dalla memoria, semplicemente non lo mostra in mappa, sarà poi
    // clusterAlgorithm a gestire la visualizzazione in mappa
    marker.map = null;
  }

  private pinCache = new Map<string, Element>();

  getMarkerContent(item: CustomerItem): Element {
    if (!this.pinCache.has(item.idPrimoCliente)) {
      const colorMap: Record<string, string> = {
        Info:   '#008941',
        Warning: '#c28a00',
        Critical:  '#ba1a1a',
      };
      const color = colorMap[item.alert?.stato ?? ''] ?? colorMap['Info'];
      const pin = new google.maps.marker.PinElement({
        background: color,
        borderColor: color,
        glyphColor: '#FFFFFF',
      });
      this.pinCache.set(item.idPrimoCliente, pin);
    }
    return this.pinCache.get(item.idPrimoCliente)!;
  }

  // La lista advisor usa i filtri attivi al momento dell'entrata in vista mappa e non si aggiorna
  // al cambio di filtri successivi (chip, ricerca, filtri avanzati).
  loadAdvisorCustomers(append = false) {
    if (this.advisorLoading) return;
    // se siamo in append e abbiamo già tutti gli item disponibili, non fare altre chiamate
    if (append && this.advisorCustomers.length >= this.advisorTotalItems && this.advisorTotalItems > 0) return;

    this.advisorLoading = true;

    const formValue = this.filtersForm.getRawValue();
    const status = this.filters.status;

    const params = {
      'Pager.PageSize': this.advisorPageSize,
      'Pager.Page': this.advisorPageIndex + 1,
    };

    const payload = {
      SearchText: { Text: formValue.searchText ?? '' },
      OrderBy: [],
      searchObject: {
        ActiveCustomers: status === CustomersType.ACTIVE,
        OrderInProgess: status === CustomersType.ORDER_IN_PROG,
        InvoicesExpiredLess30: status === CustomersType.EXPIRED_U30,
        InvoicesExpiredOver30: status === CustomersType.EXPIRED_O30,
        Agente: formValue.agenteId ? [formValue.agenteId] : [],
        Ispettore: formValue.ispettoreId ? [formValue.ispettoreId] : [],
        TipoZona: formValue.tipoZonaId ? [formValue.tipoZonaId] : [],
        ZonaId: formValue.zonaId ? [formValue.zonaId] : [],
        DataUltimaFattura: formValue.dataUltimaFattura,
        NewCustomer: formValue.newCustomer ?? false,
      }
    };

    this.http.post<any>('api/clienti/list', payload, { params }).subscribe({
      next: (data) => {
        const mapped = data.items.map((item: any) => ({
          ...item,
          zonaAgente: item.nomeAgente ? `${item.nomeZona}/${item.nomeAgente}` : `${item.nomeZona}`,
          esposizione: item.esposizione.valore,
          esposizioneStato: item.esposizione.stato,
          affidamento: item.affidamento.valore,
          affidamentoStato: item.affidamento.stato,
        }));
        if (append) {
          const existingIds = new Set(this.advisorCustomers.map(a => a.idPrimoCliente));
          this.advisorCustomers = [...this.advisorCustomers, ...mapped.filter((m: CustomerItem) => !existingIds.has(m.idPrimoCliente))];
        } else {
          this.advisorCustomers = mapped;
        }
        this.advisorTotalItems = data.totalItems;
        this.advisorLoading = false;
      },
      error: () => { this.advisorLoading = false; }
    });
  }

  onAdvisorScroll(event: Event) {
    const el = event.target as HTMLElement;
    if (el.scrollHeight - el.scrollTop <= el.clientHeight + 80 && !this.advisorLoading) {
      this.advisorPageIndex++;
      this.loadAdvisorCustomers(true);
    }
  }

  loadMapMarkers() {
    const formValue = this.filtersForm.getRawValue();
    const status = this.filters.status;

    const params = { 'Pager.PageSize': 0, 'Pager.Page': 1 };

    const payload = {
      SearchText: { Text: formValue.searchText ?? '' },
      OrderBy: [],
      searchObject: {
        ActiveCustomers: status === CustomersType.ACTIVE,
        OrderInProgess: status === CustomersType.ORDER_IN_PROG,
        InvoicesExpiredLess30: status === CustomersType.EXPIRED_U30,
        InvoicesExpiredOver30: status === CustomersType.EXPIRED_O30,
        Agente: formValue.agenteId ? [formValue.agenteId] : [],
        Ispettore: formValue.ispettoreId ? [formValue.ispettoreId] : [],
        TipoZona: formValue.tipoZonaId ? [formValue.tipoZonaId] : [],
        ZonaId: formValue.zonaId ? [formValue.zonaId] : [],
        DataUltimaFattura: formValue.dataUltimaFattura,
        NewCustomer: formValue.newCustomer ?? false,
      }
    };

    this.http.post<any>('api/clienti/list', payload, { params }).subscribe({
      next: (data) => {
        this.pinCache.clear();
        this.mapMarkers = (data.items as any[])
          .filter(item => item.latitudine != null && item.longitudine != null)
          .map(item => ({
            ...item,
            mapPosition: { lat: item.latitudine, lng: item.longitudine },
            esposizione: item.esposizione.valore,
            esposizioneStato: item.esposizione.stato,
            affidamento: item.affidamento.valore,
            affidamentoStato: item.affidamento.stato,
          }));
        this.mapMarkersLoaded = true;
      }
    });
  }

  filteredLocalita!: Observable<string[]>;
  filteredZona!: Observable<ZoneItem[]>;
  filteredTipoZona!: Observable<TipoZonaLookupItem[]>;
  filteredAgente!: Observable<AgenteLookupItem[]>;
  filteredIspettore!: Observable<IspettoreLookupItem[]>;

  filters: CustomersFilter = {};
  filtersOpen = false;
  filtersForm!: FormGroup;

  private localitaOptions: string[] = [];
  private zonaOptions: ZoneItem[] = [];
  tipoZonaOptions: TipoZonaLookupItem[] = [];
  agenteOptions: AgenteLookupItem[] = [];
  ispettoreOptions: IspettoreLookupItem[] = [];

  private dialogRef?: MatDialogRef<CustomersFilterDialogComponent>;
  private sortDialogRef?: MatDialogRef<CustomersSortDialogComponent>;

  viewOptions = [
    { value: 'table', labelKey: 'viewOptions.table', icon: 'fa-list', iconVariant: 'regular' },
    { value: 'card', labelKey: 'viewOptions.card', icon: 'fa-grid-2', iconVariant: 'regular' },
    { value: 'map', labelKey: 'viewOptions.map', icon: 'fa-map', iconVariant: 'regular' },
  ];

  selectedView = 'table';

  private breakpointObserver = inject(BreakpointObserver);
  private transloco = inject(TranslocoService);
  private bottomSheet = inject(MatBottomSheet);
  private route = inject(ActivatedRoute);

  constructor(
    public dialog: MatDialog,
    private headerService: HeaderService,
    private fb: FormBuilder,
    private chipDataService: ChipDataService,
    private zoneItemService: ZoneItemService,
    private agentiService: AgentiService,
    private ispettoriService: IspettoriService,
    private tipoZonaService: TipoZonaService,
    private http: HttpClient,
    private router: Router,
    private dynamicRouteService: DynamicRouteService,
  ) {

  }

  ngOnInit(): void {

    this.filtersForm = this.fb.group({
      searchText: [''],
      localita: [{ value: '', disabled: true }],
      agente: [''],
      agenteId: [''],
      ispettore: [''],
      ispettoreId: [''],
      tipoZona: [''],
      tipoZonaId: [''],
      zona: [''],
      zonaId: [''],
      dataUltimaFattura: [null as Date | null],
      newCustomer: [false]
    });

    this.filtersForm.get('searchText')!.valueChanges
      .pipe(debounceTime(300))
      .subscribe(() => {
        this.pageIndex = 0;
        this.loadCustomers();
      });

    this.loadCustomers();

    this.breakpointObserver
      .observe([`(min-width: ${Q8Breakpoints.desktop}px)`])
      .subscribe(result => {
        this.isDesktop = result.matches;
      });

    //Dati mockati - attendere API ?
    this.localitaOptions = [...new Set(this.customers.map(c => c.localita))];
    const filterFn = (opts: string[], value: string | null) =>
      opts.filter(o => o.toLowerCase().includes((value ?? '').toLowerCase()));

    this.filteredLocalita = this.filtersForm.get('localita')!.valueChanges.pipe(
      startWith(this.filtersForm.get('localita')!.value ?? ''),
      map(v => filterFn(this.localitaOptions, v))
    );
    this.filteredTipoZona = this.filtersForm.get('tipoZona')!.valueChanges.pipe(
      startWith(this.filtersForm.get('tipoZona')!.value ?? ''),
      map(v => this.filterTipoZonaOptions(v))
    );
    this.filteredAgente = this.filtersForm.get('agente')!.valueChanges.pipe(
      startWith(this.filtersForm.get('agente')!.value ?? ''),
      map(v => this.filterLookupOptions(this.agenteOptions, v))
    );
    this.filteredIspettore = this.filtersForm.get('ispettore')!.valueChanges.pipe(
      startWith(this.filtersForm.get('ispettore')!.value ?? ''),
      map(v => this.filterLookupOptions(this.ispettoreOptions, v))
    );

    this.agentiService.getAgenti().subscribe(response => {
      this.agenteOptions = response.agenti ?? [];
    });

    this.ispettoriService.getIspettori().subscribe(response => {
      this.ispettoreOptions = response.ispettori ?? [];
    });

    this.tipoZonaService.getTipoZone().subscribe(response => {
      this.tipoZonaOptions = response ?? [];
    });

    // Autocomplete zone
    this.zoneItemService.getZoneItem().subscribe(response => {
      this.zonaOptions = response.items ?? [];

      this.filteredZona = this.filtersForm.get('zona')!.valueChanges.pipe(
        startWith(this.filtersForm.get('zona')!.value ?? ''),
        map(v => this.filterZoneOptions(v))
      );
    });

    this.filtersForm.get('zona')!.valueChanges.subscribe(value => {
      const selectedZone = this.zonaOptions.find(zone => zone.id === this.filtersForm.get('zonaId')!.value);
      if (selectedZone?.nome !== value) {
        this.filtersForm.get('zonaId')!.setValue('', { emitEvent: false });
      }
    });

    this.filtersForm.get('agente')!.valueChanges.subscribe(value => {
      const selectedAgente = this.agenteOptions.find(option => option.value === this.filtersForm.get('agenteId')!.value);
      if (selectedAgente?.label !== value) {
        this.filtersForm.get('agenteId')!.setValue('', { emitEvent: false });
      }
    });

    this.filtersForm.get('ispettore')!.valueChanges.subscribe(value => {
      const selectedIspettore = this.ispettoreOptions.find(option => option.value === this.filtersForm.get('ispettoreId')!.value);
      if (selectedIspettore?.label !== value) {
        this.filtersForm.get('ispettoreId')!.setValue('', { emitEvent: false });
      }
    });

    this.filtersForm.get('tipoZona')!.valueChanges.subscribe(value => {
      const selectedTipoZona = this.tipoZonaOptions.find(option => option.id === this.filtersForm.get('tipoZonaId')!.value);
      if (selectedTipoZona?.descrizione !== value) {
        this.filtersForm.get('tipoZonaId')!.setValue('', { emitEvent: false });
      }
    });

    this.breakpointObserver
      .observe([`(max-width: ${Q8Breakpoints.tablet - 1}px)`])
      .subscribe(result => {
        this.isMobile = result.matches;
        // desktop/tablet -> mobile
        if (this.isMobile) {
          if (this.filtersOpen) {
            this.filtersOpen = false;
          }
      this.viewOptions = [
            { value: 'card', labelKey: 'viewOptions.card', icon: 'fa-grid-2', iconVariant: 'regular' },
            { value: 'map', labelKey: 'viewOptions.map', icon: 'fa-map', iconVariant: 'regular' },
          ];
          this.selectedView = 'card';
          this.headerService.setActions([{
            get label() { return translate<string>('customers.ui.mostraONascondiFiltri'); },
            icon: 'fa-filter',
            action: () => this.onFilterButtonClick()
          }]);
        } else {
          this.viewOptions = [
            { value: 'table', labelKey: 'viewOptions.table', icon: 'fa-list', iconVariant: 'regular' },
            { value: 'card', labelKey: 'viewOptions.card', icon: 'fa-grid-2', iconVariant: 'regular' },
            { value: 'map', labelKey: 'viewOptions.map', icon: 'fa-map', iconVariant: 'regular' },
          ];
          this.headerService.removeActions();
        }

        // mobile -> desktop/tablet
        if (!this.isMobile) {
          if (this.dialogRef) {
            this.dialogRef.close();
            this.dialogRef = undefined;
            this.filtersOpen = true;
          }
        }
      });

    // Dopo l'accesso la lista si apre sulla mappa (?view=map)
    if (this.route.snapshot.queryParamMap.get('view') === 'map') {
      this.onViewChange('map');
    }

    this.transloco.selectTranslation('customers').subscribe(() => {
      this.headerService.setTitle(this.transloco.translate('header', {}, 'customers'));
      this.translateUI();
    });
  }

  ngOnDestroy(): void{
    this.dialogRef?.close();
    this.bottomSheetRef?.dismiss();
    this.headerService.removeTitle();
    this.headerService.removeActions();
  }

  ngAfterViewInit(): void {
    google.maps.importLibrary('marker');
  }

  toggleMapAdvisor() {
    if (this.isMobile) {
      this.bottomSheetRef = this.bottomSheet.open(this.advisorSheetTemplate);
    } else {
      this.mapAdvisorOpen = !this.mapAdvisorOpen;
    }
  }

  onViewChange(value: string) {
    this.selectedView = value;
    if (value === 'map') {
      this.requestUserPosition();
      this.loadZonePolygons();
      this.loadMapMarkers();
      this.advisorPageIndex = 0;
      this.advisorCustomers = [];
      this.advisorTotalItems = 0;
      this.loadAdvisorCustomers();
      this.mapAdvisorOpen = false;
    } else {
      this.mapAdvisorOpen = false;
      this.bottomSheetRef?.dismiss();
      this.bottomSheetRef = undefined;
    }
  }

  private translateUI() {

    // Traduzione delle colonne
    this.columnsInfo = [
      {
        columnNameDef: "ragioneSociale",
        label: this.transloco.translate('table.companyName', {}, 'customers'),
        type: TypeOfColumn.TEXT,
        labelOnOneLine: true,
        sortable: true,
      },
      {
        columnNameDef: "localita",
        label: this.transloco.translate('table.city', {}, 'customers'),
        type: TypeOfColumn.TEXT,
        sortable: true,
      },
      {
        columnNameDef: "zonaAgente",
        label: this.transloco.translate('table.zoneAgent', {}, 'customers'),
        type: TypeOfColumn.TEXT,
        sortable: true,
      },
      /* {
        columnNameDef: "tipoListino",
        label: this.transloco.translate('table.listType', {}, 'customers'),
        type: TypeOfColumn.COMPONENT,
        component: ChipListino,
        sortable: false,
      }, */
      {
        columnNameDef: "esposizione",
        label: this.transloco.translate('table.exposure', {}, 'customers'),
        type: TypeOfColumn.CURRENCY,
        labelOnOneLine: true,
        sortable: true,
        cellClass: (row) => this.getCustomerStatusClass(row.esposizioneStato),
      },
      {
        columnNameDef: "affidamento",
        label: this.transloco.translate('table.creditLimit', {}, 'customers'),
        type: TypeOfColumn.CURRENCY,
        labelOnOneLine: true,
        sortable: true,
        cellClass: (row) => this.getCustomerStatusClass(row.affidamentoStato),
      },
      {
        columnNameDef: "fatturatoRolling12m",
        label: this.transloco.translate('table.revenue', {}, 'customers'),
        type: TypeOfColumn.CURRENCY,
        sortable: true,
      },
      {
        columnNameDef: "dataUltimaFattura",
        label: this.transloco.translate('table.lastInvoice', {}, 'customers'),
        type: TypeOfColumn.DATE,
        dateFormat: 'dd/MM/yyyy',
        sortable: true,
      },
      {
        columnNameDef: "alert",
        label: this.transloco.translate('table.alert', {}, 'customers'),
        type: TypeOfColumn.COMPONENT,
        component: IconAlert,
        sortable: true,
      },
      {
        columnNameDef: "detail",
        label: "",
        type: TypeOfColumn.DETAIL,
      }
    ];
  }

  openDialog(): void {
    if (this.dialogRef) {
      return;
    }

    this.dialogRef = this.dialog.open(CustomersFilterDialogComponent, {
      data: {
        value: this.filtersForm.getRawValue(),
        options: {
          localita: this.localitaOptions,
          zona: this.zonaOptions,
          tipoZona: this.tipoZonaOptions,
          agente: this.agenteOptions,
          ispettore: this.ispettoreOptions,
        }
      }
    });

    this.dialogRef.afterClosed().subscribe(result => {
      this.dialogRef = undefined;

      if (!result) {
        return;
      }

      if (result.action === 'reset') {
        this.resetFilters();
        return;
      }

      if (result.action === 'apply') {
        this.filtersForm.patchValue(result.value, { emitEvent: false });
        this.applyFilters();
      }
    });
  }

  openSortDialog(): void {
    if (this.sortDialogRef) {
      return;
    }

    this.sortDialogRef = this.dialog.open(CustomersSortDialogComponent, {
      data: { currentSort: this.sortColumnsToDialogValue() }
    });

    this.sortDialogRef.afterClosed().subscribe(result => {
      this.sortDialogRef = undefined;

      if (!result) {
        return;
      }

      if (result.action === 'reset') {
        this.currentSortColumns = [];
        this.pageIndex = 0;
        this.loadCustomers();
        return;
      }

      if (result.action === 'apply') {
        this.currentSortColumns = this.dialogValueToSortColumns(result.value);
        this.pageIndex = 0;
        this.loadCustomers();
      }
    });
  }

  private sortColumnsToDialogValue(): string | null {
    if (!this.currentSortColumns.length) return null;
    const sort = this.currentSortColumns[0];
    return `${sort.column}_${sort.direction}`;
  }

  private dialogValueToSortColumns(value: string | null): SortColumn[] {
    if (!value) return [];
    const lastUnderscore = value.lastIndexOf('_');
    const column = value.substring(0, lastUnderscore);
    const direction = value.substring(lastUnderscore + 1) as 'asc' | 'desc';
    return [{ column, direction }];
  }

  applyAndSave() {
    // this.taskFilterService.setSavedFilters(this.filters); // Salva nel service
    // this.taskListComponent?.reloadData();
    // this.loadAllCountsByStatus();
  }

  toggleAdvancedFilters(): void {
    this.filtersOpen = !this.filtersOpen;
  }

  resetFilters() {
    this.filtersForm.patchValue({
      searchText: '',
      localita: null,
      agente: null,
      agenteId: '',
      ispettore: null,
      ispettoreId: '',
      tipoZona: null,
      tipoZonaId: '',
      zona: null,
      zonaId: '',
      dataUltimaFattura: null,
      newCustomer: false,
    });
  }

  applyFilters() {
    this.pageIndex = 0;
    this.loadCustomers();
  }

  loadCustomers() {
    const endpoint = 'api/clienti/list'
    const requestId = ++this.customersLoadRequestId;
    this.customersLoading = true;

    const formValue = this.filtersForm.getRawValue();
    const status = this.filters.status;

    const params = {
      'Pager.PageSize': this.pageSize,
      'Pager.Page': this.pageIndex + 1,
    };

    const payload = {
      SearchText: {
        Text: formValue.searchText ?? ''
      },
      OrderBy: this.currentSortColumns.map(sort => ({
        PropertyName: SORT_PROPERTY_MAP[sort.column] ?? sort.column,
        OrderType: sort.direction === 'desc' ? 'Desc' : 'Asc'
      })),
      searchObject: {
        ActiveCustomers: status === CustomersType.ACTIVE,
        OrderInProgess: status === CustomersType.ORDER_IN_PROG,
        InvoicesExpiredLess30: status === CustomersType.EXPIRED_U30,
        InvoicesExpiredOver30: status === CustomersType.EXPIRED_O30,
        Agente: formValue.agenteId ? [formValue.agenteId] : [],
        Ispettore: formValue.ispettoreId ? [formValue.ispettoreId] : [],
        TipoZona: formValue.tipoZonaId ? [formValue.tipoZonaId] : [],
        ZonaId: formValue.zonaId ? [formValue.zonaId] : [],
        DataUltimaFattura: formValue.dataUltimaFattura,
        NewCustomer: formValue.newCustomer ?? false
      }
    };

    this.http.post<any>(endpoint, payload, { params }).pipe(
      finalize(() => {
        if (requestId === this.customersLoadRequestId) {
          this.customersLoading = false;
        }
      })
    ).subscribe({
      next: (data) => {
        if (requestId !== this.customersLoadRequestId) {
          return;
        }

        this.customers = data.items.map((item: any) => ({
          ...item,
          zonaAgente: item.nomeAgente ? `${item.nomeZona}/${item.nomeAgente}` : `${item.nomeZona}`,
          esposizione: item.esposizione.valore,
          esposizioneStato: item.esposizione.stato,
          affidamento: item.affidamento.valore,
          affidamentoStato: item.affidamento.stato
        }));
        this.totalItems = data.totalItems;

        if (this.selectedView === 'map') {
          this.loadMapMarkers();
        }
      },
      error: () => {
        if (requestId !== this.customersLoadRequestId) {
          return;
        }

        this.customers = [];
        this.totalItems = 0;
      }
    });

    this.chipDataService.getChipsData(payload).subscribe(data => {
      this.chipsData = data;
      this.statusChips = this.buildStatusChips(data);
    });
  }

  onStatusChipChange(event: MatChipListboxChange): void {
    this.filters.status = event.value as CustomersType | undefined;
    this.loadCustomers();
  }

  onFilterButtonClick(): void {
    if (this.isMobile) {
      this.openDialog();
      return;
    }

    this.toggleAdvancedFilters();
  }

  clearFilterField(controlName: string): void {
    const control = this.filtersForm.get(controlName);

    if (!control) {
      return;
    }

    // null (non '') così MatAutocompleteTrigger deseleziona l'opzione precedente
    control.setValue(null);

    if (controlName === 'zona') {
      this.filtersForm.get('zonaId')?.setValue('');
    }

    if (controlName === 'agente') {
      this.filtersForm.get('agenteId')?.setValue('');
    }

    if (controlName === 'ispettore') {
      this.filtersForm.get('ispettoreId')?.setValue('');
    }

    if (controlName === 'tipoZona') {
      this.filtersForm.get('tipoZonaId')?.setValue('');
    }
  }

  onZonaSelected(zone: ZoneItem) {
    this.filtersForm.patchValue({
      zona: zone.nome,
      zonaId: zone.id,
    });
  }

  onAgenteSelected(agente: AgenteLookupItem) {
    this.filtersForm.patchValue({
      agente: agente.label,
      agenteId: agente.value,
    });
  }

  onIspettoreSelected(ispettore: IspettoreLookupItem) {
    this.filtersForm.patchValue({
      ispettore: ispettore.label,
      ispettoreId: ispettore.value,
    });
  }

  onTipoZonaSelected(tipoZona: TipoZonaLookupItem) {
    this.filtersForm.patchValue({
      tipoZona: tipoZona.descrizione,
      tipoZonaId: tipoZona.id,
    });
  }

  private filterZoneOptions(value: string | null): ZoneItem[] {
    const search = value ?? '';
    return this.zonaOptions.filter(option =>
      option.nome.toLowerCase().includes(search.toLowerCase())
    );
  }

  private filterTipoZonaOptions(value: string | null): TipoZonaLookupItem[] {
    const search = value ?? '';
    return this.tipoZonaOptions.filter(option =>
      option.descrizione.toLowerCase().includes(search.toLowerCase())
    );
  }

  private filterLookupOptions<T extends { label: string }>(options: T[], value: string | null): T[] {
    const search = value ?? '';
    return options.filter(option => option.label.toLowerCase().includes(search.toLowerCase()));
  }

  buildStatusChips(data?: ChipsData): StatusChip[] {
    return [
      {
        status: CustomersType.ACTIVE,
        icon: 'fa-user',
        iconVariant: 'regular',
        labelKey: 'chipOptions.active',
        countProp: data?.clientiAttivi ?? 0
      },
      {
        status: CustomersType.ORDER_IN_PROG,
        icon: 'fa-cart-arrow-down',
        iconVariant: 'regular',
        labelKey: 'chipOptions.inProgress',
        countProp: data?.ordiniInCorso ?? 0,
        amount: data ? data.valoreOrdiniInCorso : undefined
      },
      {
        status: CustomersType.EXPIRED_U30,
        icon: 'fa-hand-holding-dollar',
        iconVariant: 'regular',
        labelKey: 'chipOptions.expiredU30',
        countProp: data?.fattureScad30 ?? 0,
        amount: data ? data.valoreFattureScad30 : undefined
      },
      {
        status: CustomersType.EXPIRED_O30,
        icon: 'fa-circle-exclamation',
        iconVariant: 'regular',
        labelKey: 'chipOptions.expiredO30',
        countProp: data?.fattureScadOltre ?? 0,
        amount: data ? data.valoreFattureScadOltre : undefined
      },
    ];
  }

  getCustomerStatusClass(status?: string | null): string {
    switch (status) {
      case 'Critical':
        return 'currency-red';
      case 'Warning':
        return 'currency-yellow';
      default:
        return 'currency-green';
    }
  }

  // Gestisce il cambio pagina dal mat-paginator condiviso
  onPageChange(event: PageEvent) {
    this.pageIndex = event.pageIndex;
    this.pageSize = event.pageSize;
    this.loadCustomers();
  }

  // Gestisce il sort emesso da ui-table-view, resetta la pagina e ricarica
  onSortChange(sortColumns: SortColumn[]) {
    this.currentSortColumns = sortColumns;
    this.pageIndex = 0;
    this.loadCustomers();
  }

  goToDetail(item: CustomerItem) {
    let navigationArray = this.dynamicRouteService.getRoutePath(this.router, CUSTOMERS_DESCRIBE, 'customer-detail');
    if (navigationArray) {
      navigationArray = this.dynamicRouteService.replacePathSegments(navigationArray, [item.idBusinessPartner]);
      this.router.navigate(navigationArray, {});
    }
  }
}
